/**
 * PDP host for the size selector: reads the Liquid data-* attributes, carries the form inputs the
 * add-to-cart flow reads, and owns the page-level side-effects (URL variant param and the
 * `variant-selected` broadcast). The control itself lives in size-selector.js so surfaces that
 * must not re-point the page — the wishlist drawer — can render it without these.
 */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import register from 'preact-custom-element'
import { SIZE_REQUIRED_EVENT, SizeSelector } from './size-selector'
import { DEFAULT_SIZE_UNIT } from 'helpers/size-units.mjs'

/**
 * `detail` is additive — the only existing listener (Omneo's useSearchQuery) just re-reads the
 * URL — and carries the stock depth the ATC block needs for its low-stock line.
 */
const broadcastSelectedVariant = (id, detail = {}) => {
  const variantSelectedEvent = new CustomEvent('variant-selected', {
    bubbles: true,
    cancelable: true,
    detail: { id, ...detail }
  })
  window.dispatchEvent(variantSelectedEvent)
}

const handleSelectedVariantParam = (id, detail = {}) => {
  window.history.replaceState({}, '', `?variant=${id}`)
  broadcastSelectedVariant(id, detail)
}

const EMPTY_SELECTION = { id: '', value: '', name: '', unavailable_stock: false, price: '' }

function ProductSelector(props) {
  const parseSizeDataObjects = useMemo(() => {
    const variants = props.dataOptions
    const parseString = variants.replaceAll("'", '"').replaceAll('\n', '').trimStart().trimEnd()
    const data = JSON.parse(parseString)
    return data
  }, [])

  /** Empty unless the request carried `?variant=` — product.selected_variant is nil without it. */
  const initialVariantId = props.dataSelectedVariant || ''

  const findRow = (id) => parseSizeDataObjects.find((variant) => String(variant.id) === String(id))

  /** Mirrors the control's state purely so the hidden inputs below can render it. */
  const [selected, setSelected] = useState(EMPTY_SELECTION)

  /**
   * The unit tab the customer is looking at, carried into the cart as a line property so the cart
   * can show the size back in the unit it was chosen in rather than the product's default.
   */
  const [sizeUnit, setSizeUnit] = useState(props.dataType || DEFAULT_SIZE_UNIT)

  /**
   * Price is carried on the variant rows rather than in the control's own state: size-selector.js
   * is shared with the wishlist drawer, which has no CTA to price.
   */
  const handleChange = (next, notify) => {
    setSelected({ ...next, price: findRow(next.id)?.price ?? '' })
    if (notify) handleSelectedVariantParam(next.id, notify)
  }

  /**
   * A seeded variant is not a user selection, so it must not rewrite the URL — which already says
   * this — but the ATC bar and the stock line sit outside this tree and learn of a variant only
   * from the broadcast. Keyed on `selected.id` rather than mount so the hidden input below has
   * committed by the time anything reads the form.
   */
  /** Merchant option relabelling from the variant_text_overrides metaobject; absent on most products. */
  const textOverrides = useMemo(() => {
    try {
      return JSON.parse(props?.dataTextOverrides || '{}')
    } catch {
      return {}
    }
  }, [props?.dataTextOverrides])

  const announced = useRef(false)
  useEffect(() => {
    if (announced.current || !initialVariantId) return
    if (String(selected.id) !== String(initialVariantId)) return
    const match = findRow(initialVariantId)
    if (!match) return
    announced.current = true
    broadcastSelectedVariant(match.id, { inventory: Number(match.inventory), available: match.available })
  }, [selected.id])

  return (
    <>
      <input name="id" value={selected.id} type="hidden" />
      {/* The CTA's price sits outside this tree; ProductForm reads it off the form like the rest. */}
      <input name="variant_price" value={selected.price} type="hidden" />
      <input name="size_unit" value={sizeUnit} type="hidden" />
      {selected.unavailable_stock && (
        <input name="unavailable_stock" value={`${selected?.value ?? selected.name}`} type="hidden" />
      )}
      <SizeSelector
        variants={parseSizeDataObjects}
        initialVariantId={initialVariantId}
        onChange={handleChange}
        initialType={props.dataType}
        onTypeChange={setSizeUnit}
        layout={props?.dataSizeLayout}
        pageType={props?.dataPdpType}
        productId={props?.dataProduct}
        showWidthAdvice={props?.dataWidthAdvice === 'true'}
        textOverrides={textOverrides}
        linkedOptions={{
          small: props?.dataSmall ?? '',
          bigger: props?.dataBigger ?? '',
          smallTitle: props?.dataSmallTitle ?? '',
          biggerTitle: props?.dataBiggerTitle ?? ''
        }}
        sizeRequiredEvent={SIZE_REQUIRED_EVENT}
        className={props.dataClass}
      />
    </>
  )
}

export default function ProductSelectPreact({ data }) {}

register(ProductSelector, 'x-product-selector')
